import { isPrepaidPlan } from "components/Membership/membershipUtil";
import {
  formatNumberAsUSCurrency,
  getNumericValueFromCurrencyString,
} from "components/Payment/paymentUtil";
import {
  PlanPrepaid,
  Plans,
  SUBSCRIPTION_PLAN_SLUGS,
} from "components/PlanSelection/planSelectionTypes";
import { values } from "lodash";
import { GetServerSidePropsContext } from "next";
import { getImagePropsForReadingLevel } from "utils/contentful/GuidedSignUpContentfulUtil";
import { getCompletedGuidedSignUpQuiz } from "utils/contentful/helpers";
import {
  getReadingLevelNameFromEntryName,
  mapImageEntryReferencesByName,
} from "utils/readingLevelImageUtils";

/**
 * Fall25 TBYB monthly plan (`one-month-2025`) still uses `boxCount: 12` in subscription-plans API
 * data; treat it as monthly for pricing/legal copy, not annual.
 */
export function isTbybMonthly2025OfferPlan(
  plan?: { slug?: string } | null
): boolean {
  return plan?.slug === SUBSCRIPTION_PLAN_SLUGS.TBYB_MONTHLY_2025;
}

export const getSelectedPlan = ({
  plans,
  plansPrepaid,
  selectedPlanSlug,
}: {
  // @note - `plans` can be refactored after prepaid test to clean this contract up a bit
  plans?: Plans | null;
  plansPrepaid?: PlanPrepaid[] | null;
  selectedPlanSlug?: string;
}) => {
  if (!selectedPlanSlug) {
    return null;
  }

  const _plans = isPrepaidPlan(selectedPlanSlug) ? plansPrepaid : values(plans);
  return _plans?.find((p) => p.slug === selectedPlanSlug) || null;
};

export const getPlanBoxImageProps = async ({
  contentfulBoxImagesRaw,
  contentfulQuizModelRaw,
  ctx,
}: {
  contentfulBoxImagesRaw: any;
  contentfulQuizModelRaw: any;
  ctx?: GetServerSidePropsContext;
}) => {
  const rawContentfulReadingLevelImages = contentfulBoxImagesRaw?.items?.[0];

  const readingLevelImages = mapImageEntryReferencesByName(
    rawContentfulReadingLevelImages,
    getReadingLevelNameFromEntryName
  );

  const completedGuidedSignUpQuiz = await getCompletedGuidedSignUpQuiz({
    cmsGuidedSignUpReferences: contentfulQuizModelRaw,
    ctx,
  });

  const readingLevelName =
    completedGuidedSignUpQuiz?.subscription?.readingLevelName?.toLowerCase() ??
    "";

  const boxImages = getImagePropsForReadingLevel({
    readingLevelName,
    readingLevelImages,
  });

  return boxImages;
};

export type PlanPrepaidPricing = ReturnType<typeof getPlanPrepaidPricing>;

export const getPlanPrepaidPricing = ({
  plan,
  plansPrepaid,
}: {
  plan?: PlanPrepaid;
  plansPrepaid?: PlanPrepaid[] | null;
}) => {
  const basePrice = plansPrepaid?.find((p) => p.boxCount === 1)?.amount || null;
  const basePriceNumber = basePrice
    ? getNumericValueFromCurrencyString(String(basePrice))
    : null;
  const basePriceCurrencyString =
    typeof basePriceNumber === "number"
      ? formatNumberAsUSCurrency(basePriceNumber)
      : null;

  const boxCount = plan?.boxCount || null;

  const priceWithoutSavingsNumber =
    typeof basePriceNumber === "number" && boxCount
      ? basePriceNumber * boxCount
      : null;
  const priceWithoutSavingsCurrencyString =
    typeof priceWithoutSavingsNumber === "number"
      ? formatNumberAsUSCurrency(priceWithoutSavingsNumber)
      : null;

  const selectedPlanCostPerBox = plan?.costPerBox || null;

  const boxSavingsNumber = plan?.savingsAmount
    ? getNumericValueFromCurrencyString(plan.savingsAmount)
    : null;
  const boxSavingsCurrencyString =
    boxSavingsNumber && boxSavingsNumber > 0 ? plan?.savingsAmount : null;

  const priceWithBoxSavingsNumber =
    typeof priceWithoutSavingsNumber === "number" &&
    typeof boxSavingsNumber === "number"
      ? priceWithoutSavingsNumber - boxSavingsNumber
      : null;
  const priceWithBoxSavingsCurrencyString =
    typeof priceWithBoxSavingsNumber == "number"
      ? formatNumberAsUSCurrency(priceWithBoxSavingsNumber)
      : null;

  const calculatedTotal = boxSavingsNumber || 0;
  const totalSavingsNumber = calculatedTotal > 0 ? calculatedTotal : null;
  const totalSavingsCurrencyString = totalSavingsNumber
    ? formatNumberAsUSCurrency(totalSavingsNumber)
    : null;

  return {
    basePriceCurrencyString,
    boxCount,
    boxSavingsNumber,
    boxSavingsCurrencyString,
    priceWithBoxSavingsNumber,
    priceWithBoxSavingsCurrencyString,
    priceWithoutSavingsNumber,
    priceWithoutSavingsCurrencyString,
    selectedPlanCostPerBox,
    totalSavingsCurrencyString,
    totalSavingsNumber,
  };
};
